module Client.Home.UpdatePassword

open System
open Shared
open Client
open Elmish
open Illy.SafeStack
open Illy.SafeStack.Client
open Fable.React 
open Fable.React.Props 
open Shared.Home.UpdatePassword

type Model = 
    {
        CurrentPassword: Text.Model
        NewPassword: ScorablePassword.Model
        ConfirmNewPassword: Text.Model
        Loading: bool
        Saving: bool
    }

let toDTO (model: Model) = 
    {
        CurrentPassword = Text.toString model.CurrentPassword
        NewPassword = ScorablePassword.toString model.NewPassword
        ConfirmNewPassword = Text.toString model.ConfirmNewPassword
    }

module Validations = 
    open Illy.SafeStack.Validation
    open Shared.Home.UpdatePassword.Validations

    let resetValidations (model: Model) = 
        {model with 
            CurrentPassword = Text.resetValidations model.CurrentPassword
            NewPassword = ScorablePassword.resetValidations model.NewPassword
            ConfirmNewPassword = Text.resetValidations model.ConfirmNewPassword
        }

    let dispatchValidations model = 
        let model = resetValidations model 
        function 
        | ValidationResult.Passed -> model 
        | ValidationResult.Warned _ -> failwith "Did not expect warnings"
        | ValidationResult.Failed (err, _) ->
            let errors = 
                err 
                |> ValidationInfrastructure.bundleValidations 
                    (fun x -> 
                        (match x with 
                        | CurrentPasswordValidations _ -> 0 
                        | NewPasswordValidations _ -> 1
                        | ConfirmPasswordValidations _ -> 2), UpdatePasswordValidations.toString x)
                    3
            {model with 
                CurrentPassword = Text.setValidations model.CurrentPassword errors.[0] None
                NewPassword = ScorablePassword.setValidations model.NewPassword errors.[1] None
                ConfirmNewPassword = Text.setValidations model.ConfirmNewPassword errors.[2] None
            }

type Msg = 
    | ApiError of ApiError
    | SaveResult of ROP.Result<ClientSideUser, Home.UpdatePassword.FailureReason>
    | CurrentPasswordMsg of Text.Msg
    | NewPasswordMsg of ScorablePassword.Msg
    | ConfirmNewPasswordMsg of Text.Msg
    | CloseOverlay  
    | SaveClicked 
    | InitDataLoaded of Shared.Zxcvbn.PasswordConfig

type Event = 
    | CloseOverlay 
    | ApiError of ApiError 
    | SaveSuccessful of ClientSideUser * notification: string 

module Initialisations = 
    let initCurrentPassword (config: Zxcvbn.PasswordConfig) value =
        let length = config.PasswordStrength.MaxLength |> Option.defaultValue 250 |> float
        {Text.CustomSettings.defaultOptions() with TextType = Text.TextType.Password}
        |> Text.init "CurrentPassword" "Current Password" (Some "Enter Current Password") length true value
    
    let initNewPassword config value =
        ScorablePassword.init 
            {ScorablePassword.Settings.defaults with 
                Config = config
                Label = "New Password"
            } 
            value
    let initConfirmNewPassword (config: Zxcvbn.PasswordConfig) value =
        let length = config.PasswordStrength.MaxLength |> Option.defaultValue 250 |> float
        {Text.CustomSettings.defaultOptions() with TextType = Text.TextType.Password}
        |> Text.init "ConfirmNewPassword" "Confirm New Password" (Some "Confirm New password") length true value

let init () = 
    {
        CurrentPassword = Initialisations.initCurrentPassword Zxcvbn.PasswordConfig.defaults None
        NewPassword = Initialisations.initNewPassword Zxcvbn.PasswordConfig.defaults None
        ConfirmNewPassword = Initialisations.initConfirmNewPassword Zxcvbn.PasswordConfig.defaults None
        Loading = true
        Saving = false
    }, Api.secureCall<IUpdatePasswordApi,_,_,_> SecuredRoute.Standard (fun api -> api.getInitData) () InitDataLoaded Msg.ApiError 

let update msg model = 
    match msg with 
    | InitDataLoaded pwdConfig -> 
        {model with 
            Loading = false
            CurrentPassword = Initialisations.initCurrentPassword pwdConfig None
            NewPassword = Initialisations.initNewPassword pwdConfig None
            ConfirmNewPassword = Initialisations.initConfirmNewPassword pwdConfig None
        }, SideEffect.none
    | CurrentPasswordMsg cMsg ->
        let m, c = Text.update cMsg model.CurrentPassword
        {model with CurrentPassword = m}, Cmd.map CurrentPasswordMsg c |> SideEffect.Cmd
    | NewPasswordMsg cMsg ->
        let m, c = ScorablePassword.update cMsg model.NewPassword
        {model with NewPassword = m}, Cmd.map NewPasswordMsg c |> SideEffect.Cmd
    | ConfirmNewPasswordMsg cMsg ->
        let m, c = Text.update cMsg model.ConfirmNewPassword
        {model with ConfirmNewPassword = m}, Cmd.map ConfirmNewPasswordMsg c |> SideEffect.Cmd
    | Msg.ApiError err -> model, err |> ApiError |> SideEffect.Event 
    | Msg.CloseOverlay -> model, CloseOverlay |> SideEffect.Event 
    | SaveClicked -> 
        let dto = toDTO model 
        let vResult =
            Validations.validateDTO
                Zxcvbn.scorePassword
                model.NewPassword.Settings.Config
                dto
        if Validation.isSuccessful false vResult then 
            {model with Saving = true},
            Api.secureCall<IUpdatePasswordApi,_,_,_> SecuredRoute.Standard (fun api -> api.updatePassword) dto SaveResult Msg.ApiError
            |> SideEffect.Cmd
        else 
            Validations.dispatchValidations model vResult, SideEffect.none
    | SaveResult result -> 
        match result with 
        | ROP.Success u ->
            model, (u, "Password was updated successfully.") |> Event.SaveSuccessful |> SideEffect.Event
        | ROP.Failure (FailedValidations v) ->
            Validations.dispatchValidations {model with Saving = false} v, SideEffect.none

let view model dispatch = 
    jQueryUI.modal "Update Password" 50 Msg.CloseOverlay dispatch (Some 600) [
        yield Text.text model.Loading model.CurrentPassword (CurrentPasswordMsg >> dispatch)
        yield! ScorablePassword.view model.Loading model.NewPassword (NewPasswordMsg >> dispatch)
        yield Text.text model.Loading model.ConfirmNewPassword (ConfirmNewPasswordMsg >> dispatch)
        yield! UI.saveControls (Some "Update password") model.Loading model.Saving SaveClicked Msg.CloseOverlay dispatch
    ]